import React, { useState } from 'react';
import { ClientData } from '../types';
import { Server, Activity, TrendingUp, Search, MessageSquare, Globe } from 'lucide-react';
import ModuleLabels from './ModuleLabels';
import SuccessRate from './SuccessRate';
import './ClientsTable.css';

interface ClientsTableProps {
  clients: ClientData[];
  loading: boolean;
  year: number;
  month: number;
  onClientUpdate?: (clientName: string, updatedData: Partial<ClientData>) => void;
}

const ClientsTable: React.FC<ClientsTableProps> = ({ clients, loading, year, month, onClientUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof ClientData>('monthlyTotal');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [commentText, setCommentText] = useState('');

  // Filtrar clientes por término de búsqueda
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.ipAddress.includes(searchTerm)
  );

  // Ordenar clientes
  const sortedClients = [...filteredClients].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  const handleSort = (field: keyof ClientData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getMonthName = (month: number) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1];
  };

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return 'N/A';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return `${start.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short' 
    })} - ${end.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short' 
    })}`;
  };

  // Verificar si estamos en vista de ciclo
  const isCycleView = clients.length > 0 && clients[0].isCycleView;

  const getStatusBadge = (client: ClientData) => {
    if (client.clientStatus === 'demo') {
      const daysLeft = client.daysRemainingDemo;
      const isExpiringSoon = daysLeft !== null && daysLeft !== undefined && daysLeft <= 7;
      return (
        <span className={`status-badge demo ${isExpiringSoon ? 'expiring' : ''}`}>
          Demo {daysLeft !== null && daysLeft !== undefined ? `(${daysLeft}d)` : ''}
        </span>
      );
    } else if (client.clientStatus === 'paid') {
      const membershipText = client.membershipStatus === 1 ? 'AL DIA' : 
                             client.membershipStatus === 0 ? 'DESACTIVADO' : '';
      return (
        <span className={`status-badge paid ${client.membershipStatus === 0 ? 'deactivated' : ''}`}>
          Cliente Pago {membershipText && `(${membershipText})`}
        </span>
      );
    } else if (client.clientStatus === 'demo_terminated') {
      const daysExpired = client.daysSinceDemoExpired;
      return (
        <span className="status-badge demo-terminated">
          Demo Terminado {daysExpired !== null && daysExpired !== undefined ? `(hace ${daysExpired}d)` : ''}
        </span>
      );
    } else if (client.clientStatus === 'terminated') {
      return <span className="status-badge terminated">Terminado</span>;
    } else {
      return <span className="status-badge inactive">Inactivo</span>;
    }
  };

  const getPaymentTypeBadge = (client: ClientData) => {
    if (Boolean(client.isUniquePayment)) {
      return <span className="payment-type-badge package">Paquete</span>;
    } else {
      return <span className="payment-type-badge recurrent">Recurrente</span>;
    }
  };

  const getLastInvoiceInfo = (client: ClientData) => {
    if (!client.lastInvoiceDate) {
      return <span className="no-invoice-data">Sin facturas</span>;
    }

    const invoiceDate = new Date(client.lastInvoiceDate);
    const today = new Date();
    
    // Obtener fecha actual en Colombia
    const todayInColombia = new Date(today.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    
    // Usar las fechas UTC de la factura (que ya representan el día correcto)
    const invoiceDay = invoiceDate.getUTCDate();
    const invoiceMonth = invoiceDate.getUTCMonth();
    const invoiceYear = invoiceDate.getUTCFullYear();
    
    // Fecha actual en Colombia
    const todayDay = todayInColombia.getDate();
    const todayMonth = todayInColombia.getMonth();
    const todayYear = todayInColombia.getFullYear();
    
    // Crear fechas normalizadas para comparación
    const invoiceNormalized = new Date(invoiceYear, invoiceMonth, invoiceDay);
    const todayNormalized = new Date(todayYear, todayMonth, todayDay);
    
    const diffTime = todayNormalized.getTime() - invoiceNormalized.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let timeAgo = '';
    if (diffDays === 0) {
      timeAgo = 'Hoy';
    } else if (diffDays === 1) {
      timeAgo = 'Ayer';
    } else if (diffDays === 2) {
      timeAgo = 'Hace 2 días';
    } else if (diffDays < 7) {
      timeAgo = `Hace ${diffDays} días`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      timeAgo = `Hace ${weeks} semana${weeks > 1 ? 's' : ''}`;
    } else {
      const months = Math.floor(diffDays / 30);
      timeAgo = `Hace ${months} mes${months > 1 ? 'es' : ''}`;
    }

    return (
      <div className="last-invoice-info">
        <div className="invoice-date">{timeAgo}</div>
        <div className="invoice-count">{client.dailyInvoiceCount} factura{client.dailyInvoiceCount !== 1 ? 's' : ''}</div>
      </div>
    );
  };

  const getUsageBar = (client: ClientData) => {
    if (!client.numberOfContractedInvoices || client.numberOfContractedInvoices === 0) {
      return null;
    }

    const percentage = client.usagePercentage || 0;
    const isOverLimit = percentage > 80;
    
    return (
      <div className="usage-progress">
        <div className="usage-numbers">
          <span className="current">{client.monthlyTotal}</span>
          <span className="separator">/</span>
          <span className="contracted">{client.numberOfContractedInvoices}</span>
        </div>
        <div className="progress-bar">
          <div 
            className={`progress-fill ${isOverLimit ? 'over-limit' : ''}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <span className="percentage">{percentage.toFixed(1)}%</span>
      </div>
    );
  };

  // Funciones para manejar comentarios
  const handleAddComment = (client: ClientData) => {
    setSelectedClient(client);
    setCommentText('');
    setShowCommentModal(true);
  };

  const handleEditComment = (client: ClientData) => {
    setSelectedClient(client);
    setCommentText(client.comment || '');
    setShowCommentModal(true);
  };

  const handleSaveComment = async () => {
    if (!selectedClient) return;

    try {
      const response = await fetch(`/api/clients/${selectedClient.name}/comment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment: commentText.trim() || null }),
      });

      if (response.ok) {
        const updatedComment = commentText.trim() || null;
        
        // Actualizar el estado local si tenemos la función callback
        if (onClientUpdate) {
          onClientUpdate(selectedClient.name, { comment: updatedComment });
        }
        
        setShowCommentModal(false);
        setSelectedClient(null);
        setCommentText('');
      } else {
        console.error('Error al guardar el comentario');
        alert('Error al guardar el comentario');
      }
    } catch (error) {
      console.error('Error al guardar el comentario:', error);
      alert('Error al guardar el comentario');
    }
  };

  const handleCopyIP = async (ipAddress: string, clientName: string) => {
    try {
      await navigator.clipboard.writeText(ipAddress);
      // Mostrar feedback visual temporal
      const element = document.querySelector(`[data-client="${clientName}"] .ip-address`);
      if (element) {
        element.classList.add('copied');
        setTimeout(() => {
          element.classList.remove('copied');
        }, 1000);
      }
    } catch (err) {
      console.error('Error copying IP to clipboard:', err);
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = ipAddress;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleCancelComment = () => {
    setShowCommentModal(false);
    setSelectedClient(null);
    setCommentText('');
  };

  const totalUsage = clients.reduce((sum, client) => sum + client.monthlyTotal, 0);
  const demoClients = clients.filter(client => client.clientStatus === 'demo').length;
  const paidClients = clients.filter(client => client.clientStatus === 'paid').length;
  const expiringSoonDemos = clients.filter(client => 
    client.clientStatus === 'demo' && 
    client.daysRemainingDemo !== null && 
    client.daysRemainingDemo !== undefined &&
    client.daysRemainingDemo <= 7
  ).length;

  if (loading) {
    return (
      <div className="clients-table-container">
        <div className="loading-state">
          <Activity className="loading-spinner" size={32} />
          <p>Cargando datos de clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-table-container">
      {/* Header con estadísticas */}
      <div className="table-header">
                      <div className="table-title">
                <Server size={24} />
                <h2>
                  Uso de Clientes - {getMonthName(month)} {year}
                  {isCycleView && <span className="view-indicator">(Vista por Ciclo)</span>}
                </h2>
              </div>
        
        <div className="table-stats">
          <div className="stat-item">
            <span className="stat-label">Total Clientes:</span>
            <span className="stat-value">{clients.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Demos:</span>
            <span className="stat-value demo">{demoClients}</span>
            {expiringSoonDemos > 0 && (
              <span className="stat-warning">({expiringSoonDemos} expiran pronto)</span>
            )}
          </div>
          <div className="stat-item">
            <span className="stat-label">Clientes Pago:</span>
            <span className="stat-value paid">{paidClients}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Uso Total:</span>
            <span className="stat-value">{totalUsage.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="search-bar">
        <Search size={20} />
        <input
          type="text"
          placeholder="Buscar por nombre o IP..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Tabla */}
      <div className="table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('name')}
                className={`sortable ${sortField === 'name' ? sortDirection : ''}`}
              >
                Cliente
                <TrendingUp size={14} className="sort-icon" />
              </th>
              <th>Métricas</th>
              <th>Estado</th>
              <th>Tipo de Pago</th>
              <th>Última Factura</th>
                                    <th 
                        onClick={() => handleSort('monthlyTotal')}
                        className={`sortable ${sortField === 'monthlyTotal' ? sortDirection : ''}`}
                      >
                        {isCycleView ? 'Uso en Ciclo vs Contratado' : 'Uso vs Contratado'}
                        <TrendingUp size={14} className="sort-icon" />
                      </th>
              <th title="IP Address"><Globe size={16} /></th>
              {isCycleView && <th>Rango del Ciclo</th>}
            </tr>
          </thead>
          <tbody>
            {sortedClients.map((client) => (
              <tr key={client.name} className={`${!client.hasData ? 'no-data' : ''} ${client.clientStatus}`} data-client={client.name}>
                <td className="client-name">
                  <div className="client-info">
                    <span className="name">
                      {client.name}
                    </span>
                    {client.comment ? (
                      <div className="client-comment" onClick={() => handleEditComment(client)}>
                        <span className="comment-text">{client.comment}</span>
                      </div>
                    ) : (
                      <div className="add-comment-icon" onClick={() => handleAddComment(client)} title="Agregar comentario">
                        <MessageSquare size={14} />
                      </div>
                    )}
                  </div>
                </td>
                <td className="metrics-cell">
                  <div className="metrics-info">
                    <span className="pending-count">
                      {client.metrics?.countPendingForSend || 0}
                    </span>
                    <span className="separator">|</span>
                    <SuccessRate 
                      successRate={client.successRate || 0} 
                      metrics={client.metrics}
                    />
                  </div>
                </td>
                <td className="status-cell">
                  <div className="status-with-modules">
                    {getStatusBadge(client)}
                    <ModuleLabels modules={client.modulesEnabled || []} compact={true} />
                  </div>
                </td>
                <td className="payment-type-cell">
                  {getPaymentTypeBadge(client)}
                </td>
                <td className="last-invoice-cell">
                  {getLastInvoiceInfo(client)}
                </td>
                <td className="usage-cell">
                  {getUsageBar(client) || (
                    <div className="simple-usage">
                      <span className="usage-number">
                        {client.monthlyTotal.toLocaleString()}
                      </span>
                      {client.hasData && (
                        <div className="usage-bar">
                          <div 
                            className="usage-fill"
                            style={{ 
                              width: `${Math.min((client.monthlyTotal / Math.max(...clients.map(c => c.monthlyTotal))) * 100, 100)}%` 
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="ip-address" onClick={() => handleCopyIP(client.ipAddress, client.name)}>
                  <Globe size={16} />
                </td>
                {isCycleView && (
                  <td className="cycle-range">
                    {formatDateRange(client.cycleStartDate || null, client.cycleEndDate || null)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {sortedClients.length === 0 && (
          <div className="empty-state">
            <Search size={48} />
            <h3>No se encontraron clientes</h3>
            <p>Intenta con otros términos de búsqueda</p>
          </div>
        )}
      </div>

      {/* Modal de comentarios */}
      {showCommentModal && (
        <div className="modal-overlay" onClick={handleCancelComment}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedClient?.comment ? 'Editar comentario' : 'Agregar comentario'} - {selectedClient?.name}
              </h3>
            </div>
            <div className="modal-body">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Escribe un comentario para este cliente..."
                rows={4}
                maxLength={500}
                autoFocus
              />
              <div className="character-count">
                {commentText.length}/500 caracteres
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancel" 
                onClick={handleCancelComment}
              >
                Cancelar
              </button>
              <button 
                className="btn-save" 
                onClick={handleSaveComment}
                disabled={commentText.trim().length === 0 && !selectedClient?.comment}
              >
                {selectedClient?.comment ? 'Actualizar' : 'Guardar'}
              </button>
              {selectedClient?.comment && (
                <button 
                  className="btn-delete" 
                  onClick={() => {
                    setCommentText('');
                    handleSaveComment();
                  }}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsTable;
